import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  ReactNode,
} from "react";
import AlertModal from "../components/AlertModal/AlertModal";

export interface AlertOptions {
  title?: string;
  message: string;
  type?: AlertType;
}

export interface ConfirmOptions extends AlertOptions {
  confirmText?: string;
  cancelText?: string;
}

export type AlertType = "info" | "success" | "error" | "warning";

export interface AlertContextType {
  alert: (options: AlertOptions) => Promise<void>;
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

export interface AlertState {
  mode: "alert" | "confirm";
  title?: string;
  message: string;
  type: AlertType;
  confirmText: string;
  cancelText: string;
  resolve: (value: boolean) => void;
}

export function getAccentColor(type: AlertType): string {
  switch (type) {
    case "info":
      return "var(--color-accent)";
    case "success":
      return "var(--color-highlight)";
    case "error":
      return "var(--color-danger, #ef4444)";
    case "warning":
      return "var(--color-accent-alt)";
  }
}

export const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const AlertProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AlertState | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const queueRef = useRef<Promise<boolean>>(Promise.resolve(false));

  // Body scroll lock
  useEffect(() => {
    if (state) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [state]);

  // Store previously-focused element when modal opens
  useEffect(() => {
    if (state) {
      previousFocusRef.current = document.activeElement as HTMLElement | null;
    }
  }, [state]);

  const handleClose = useCallback((result: boolean) => {
    setState((current) => {
      if (current) {
        current.resolve(result);
      }
      return null;
    });

    // Restore focus after the modal is removed from the DOM
    setTimeout(() => {
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    }, 0);
  }, []);

  const alert = useCallback((options: AlertOptions): Promise<void> => {
    const pending = queueRef.current;

    const next = pending.then(() => {
      return new Promise<boolean>((resolve) => {
        setState({
          mode: "alert",
          message: options.message,
          title: options.title,
          type: options.type || "info",
          confirmText: "OK",
          cancelText: "Cancel",
          resolve,
        });
      });
    });

    queueRef.current = next;
    return next.then(() => undefined);
  }, []);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    const pending = queueRef.current;

    const next = pending.then(() => {
      return new Promise<boolean>((resolve) => {
        setState({
          mode: "confirm",
          message: options.message,
          title: options.title,
          type: options.type || "info",
          confirmText: options.confirmText || "OK",
          cancelText: options.cancelText || "Cancel",
          resolve,
        });
      });
    });

    queueRef.current = next;
    return next;
  }, []);

  return (
    <AlertContext.Provider value={{ alert, confirm }}>
      {children}
      {state && <AlertModal state={state} onClose={handleClose} />}
    </AlertContext.Provider>
  );
};

export const useAlert = (): AlertContextType => {
  const ctx = useContext(AlertContext);
  if (!ctx) throw new Error("useAlert must be used within an AlertProvider");
  return ctx;
};
